import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import type { FieldDef, FieldOption, FormValues } from '../types';

interface TagsFieldProps {
  field: FieldDef;
  values: FormValues;
  onChange: (key: string, value: string[]) => void;
}

const filter = createFilterOptions<FieldOption>();

function toTags(raw: string | string[] | undefined): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw
    .split(/[\n,;]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function getConflicts(tags: string[], otherRaw: string | string[] | undefined): Set<string> {
  const other = new Set(toTags(otherRaw));
  return new Set(tags.filter((t) => other.has(t)));
}

export default function TagsField({ field, values, onChange }: TagsFieldProps) {
  const tags = toTags(values[field.key] as string | string[] | undefined);
  const otherRaw = field.excludeValuesFrom ? (values[field.excludeValuesFrom] as string | string[] | undefined) : undefined;
  const conflicts = otherRaw !== undefined ? getConflicts(tags, otherRaw) : new Set<string>();
  const hasConflict = conflicts.size > 0;

  const options: FieldOption[] = field.options ?? [];
  const excludedValues = new Set(toTags(otherRaw));
  const availableOptions = options.filter((o) => !excludedValues.has(o.value));

  const selectedOptions: FieldOption[] = tags.map((t) => {
    const found = options.find((o) => o.value === t);
    return found ?? { value: t, label: t };
  });

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={availableOptions}
      value={selectedOptions}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const inputValue = params.inputValue.trim();
        if (inputValue && !opts.some((o) => o.value === inputValue || o.label.toLowerCase() === inputValue.toLowerCase())) {
          filtered.push({ value: inputValue, label: `Adicionar "${inputValue}"` });
        }
        return filtered;
      }}
      onChange={(_e, newValue) => {
        const newTags = newValue.map((v) => {
          if (typeof v === 'string') return v;
          if (v.label.startsWith('Adicionar "')) return v.value;
          return v.value;
        });
        onChange(field.key, newTags);
      }}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              label={typeof opt === 'string' ? opt : opt.label.startsWith('Adicionar "') ? opt.value : opt.label}
              size="small"
              color={conflicts.has(typeof opt === 'string' ? opt : opt.value) ? 'error' : 'default'}
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          required={field.required}
          placeholder={tags.length === 0 ? (field.placeholder ?? 'Selecione ou digite') : undefined}
          error={hasConflict}
          helperText={
            hasConflict
              ? `Valor(es) em conflito com o campo oposto: ${[...conflicts].join(', ')}`
              : field.helperText
          }
        />
      )}
    />
  );
}
